import { GiveawayEntry } from '@psl/flows/showcase/pages/giveaway/functions/types';
import { equals } from 'ramda';
import { map, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { filter, throttleTime } from 'rxjs/operators';
import {
  LiveStreamGiveawayItemApi,
  UpcomingShowGiveawayItemApi,
} from '../../generated/api';
import { ErrorReasons } from '../../models/ApiErrors';
import { GiveawayItem, GiveawayItemStatus } from '../../models/ClientModels';
import { skipForUnauthenticatedUsers } from '../auth/utils';
import { EntityRef, entityRefSelect } from '../EntityRef';
import { firestore } from '../firebase/firebaseAccess';
import {
  collectionQuery,
  createObservableQuery,
  docQuery,
  ObservableQuery,
  snapshotToDataWithId,
  WithID,
} from '../firebase/rxfireExtensions';
import { ListingFirestoreHelper } from '../listing/ListingFirestoreHelper';
import { APIErrorWrapper } from '../services';
import { isErrorInfo } from '../services/middlewares/ErrorWrappingMiddleware';
import {
  fromLiveStreamCreateGiveawayItemRequestCriteriaEnum,
  GiveawayCondition,
  toLiveStreamCreateGiveawayItemRequestCriteriaEnum,
  toUpcomingShowCreateGiveawayItemRequestCriteriaEnum,
} from './_/GiveawayCondition';
import { transformServerGiveawayEntrant } from './_/serverGiveawayItemHelpers';

export { GiveawayCondition } from './_/GiveawayCondition';

export type GiveawayAvailableShowRef =
  | EntityRef.LiveShow
  | EntityRef.UpcomingShow;
export function isGiveawayAvailableShowRef(
  showRef: EntityRef,
): showRef is GiveawayAvailableShowRef {
  return showRef.type === 'liveShow' || showRef.type === 'upcomingShow';
}

export class GiveawayServiceHelper {
  constructor(
    private listingSvcHelper: ListingFirestoreHelper,
    private liveStreamGiveawayItemSvc: LiveStreamGiveawayItemApi,
    private upcomingShowGiveawayItemSvc: UpcomingShowGiveawayItemApi,
  ) {}

  activeGiveawayId(
    showRef: GiveawayAvailableShowRef,
  ): Observable<undefined | string> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .where('status', '==', GiveawayItemStatus.created),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .where('status', '==', GiveawayItemStatus.created),
    });
    return collectionQuery(query).pipe(map(query => query.docs[0]!.id));
  }

  activeGiveaway(
    showRef: GiveawayAvailableShowRef,
  ): Observable<undefined | WithID<GiveawayItem>> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .where('status', '==', GiveawayItemStatus.created),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .where('status', '==', GiveawayItemStatus.created),
    });
    return collectionQuery(query).pipe(
      map(query =>
        query.docs[0]?.data() != null
          ? snapshotToDataWithId(query.docs[0]!)!
          : undefined,
      ),
    );
  }

  getGiveawayById(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): Observable<undefined | WithID<GiveawayItem>> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .doc(giveawayId),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .doc(giveawayId),
    });

    return docQuery(query).pipe(
      map(snap =>
        snap.data() != null ? snapshotToDataWithId(snap)! : undefined,
      ),
    );
  }

  getGiveawayByIdWithAutoUpdatedListing(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): Observable<undefined | WithID<GiveawayItem>> {
    // TODO: implement it
    return this.getGiveawayById(showRef, giveawayId);
  }

  getGiveawayEntries(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): ObservableQuery<any, WithID<GiveawayEntry>> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants'),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants'),
    });

    return createObservableQuery(
      query,
      map((snap): WithID<GiveawayEntry>[] =>
        snap.docs.map(d => transformServerGiveawayEntrant(d.id, d.data()!)),
      ),
    );
  }

  getAllGiveawayEntries(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): Observable<WithID<GiveawayEntry>[]> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants'),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants'),
    });

    return collectionQuery(query).pipe(
      map((snap): WithID<GiveawayEntry>[] =>
        snap.docs.map(d => transformServerGiveawayEntrant(d.id, d.data()!)),
      ),
    );
  }

  getGiveawayHistoricalWinners(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): ObservableQuery<any, WithID<GiveawayEntry>> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants')
          .where('isWinner', '==', true),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants')
          .where('isWinner', '==', true),
    });

    return createObservableQuery(
      query,
      map((snap): WithID<GiveawayEntry>[] =>
        snap.docs.map(d => transformServerGiveawayEntrant(d.id, d.data()!)),
      ),
    );
  }

  private unmatchedConditionsMayChangedGiveawayIds = new Subject<
    [showRef: GiveawayAvailableShowRef, giveawayId: string][]
  >();

  notifyUnmatchedConditionsMayChanged(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): void {
    this.unmatchedConditionsMayChangedGiveawayIds.next([[showRef, giveawayId]]);
  }

  getGiveawayUnmatchedConditions(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): Observable<GiveawayCondition[]> {
    const check = entityRefSelect(showRef, {
      liveShow: s => (uid: string) =>
        this.liveStreamGiveawayItemSvc.liveStreamGiveawayItemApiControllerCheckEntrantEligibility(
          {
            liveStreamId: s.liveStreamId,
            giveawayItemId: giveawayId,
            entrantId: uid,
          },
        ),
      upcomingShow: s => (uid: string) =>
        this.upcomingShowGiveawayItemSvc.upcomingShowGiveawayItemApiControllerCheckEntrantEligibility(
          {
            upcomingShowId: s.upcomingShowId,
            giveawayItemId: giveawayId,
            entrantId: uid,
          },
        ),
    });

    return skipForUnauthenticatedUsers(
      uid =>
        merge(
          of(undefined),
          this.unmatchedConditionsMayChangedGiveawayIds.pipe(
            filter(ids => ids.some(id => equals(id, [showRef, giveawayId]))),
          ),
        ).pipe(
          throttleTime(1000, undefined, { trailing: true }),
          switchMap(() => this.getGiveawayById(showRef, giveawayId)),
          switchMap(giveaway =>
            giveaway == null || giveaway.status === GiveawayItemStatus.finished
              ? of([])
              : check(uid).then(
                  () => [],
                  err =>
                    transformConditionError(
                      fromLiveStreamCreateGiveawayItemRequestCriteriaEnum as any,
                      err,
                    ),
                ),
          ),
        ),
      [],
    );
  }

  getIsJoinedGiveaway(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
    userId: string,
  ): Observable<undefined | {}> {
    const query = entityRefSelect(showRef, {
      liveShow: s =>
        firestore()
          .collection('liveStreams')
          .doc(s.liveStreamId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants')
          .doc(userId),
      upcomingShow: s =>
        firestore()
          .collection('upcomingShows')
          .doc(s.upcomingShowId)
          .collection('giveawayItems')
          .doc(giveawayId)
          .collection('entrants')
          .doc(userId),
    });

    return docQuery(query).pipe(map(snap => (snap.exists ? {} : undefined)));
  }

  getIsCurrentUserJoinedGiveaway(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
  ): Observable<undefined | {}> {
    return skipForUnauthenticatedUsers(
      uid => this.getIsJoinedGiveaway(showRef, giveawayId, uid),
      false,
    );
  }

  async createGiveaway(
    showRef: GiveawayAvailableShowRef,
    giveawayInfo: {
      images: string[];
      name: string;
      description?: string;
      listingId: string;
      variantId?: string;
      condition?: GiveawayCondition;
    },
  ) {
    const sendRequest = entityRefSelect(showRef, {
      liveShow: s => () =>
        this.liveStreamGiveawayItemSvc.liveStreamGiveawayItemApiControllerCreateGiveawayItem(
          {
            liveStreamId: s.liveStreamId,
            liveStreamCreateGiveawayItemRequest: {
              coverImages: giveawayInfo.images,
              title: giveawayInfo.name,
              description: giveawayInfo.description,
              listingId: giveawayInfo.listingId,
              variantId: giveawayInfo.variantId,
              criteria:
                giveawayInfo.condition == null
                  ? []
                  : [
                      toLiveStreamCreateGiveawayItemRequestCriteriaEnum(
                        giveawayInfo.condition,
                      ),
                    ],
            },
          },
        ),
      upcomingShow: s => () =>
        this.upcomingShowGiveawayItemSvc.upcomingShowGiveawayItemApiControllerCreateGiveawayItem(
          {
            upcomingShowId: s.upcomingShowId,
            upcomingShowCreateGiveawayItemRequest: {
              coverImages: giveawayInfo.images,
              title: giveawayInfo.name,
              description: giveawayInfo.description,
              listingId: giveawayInfo.listingId,
              variantId: giveawayInfo.variantId,
              criteria:
                giveawayInfo.condition == null
                  ? []
                  : [
                      toUpcomingShowCreateGiveawayItemRequestCriteriaEnum(
                        giveawayInfo.condition,
                      ),
                    ],
            },
          },
        ),
    });

    await sendRequest();
  }

  async assignGiveawayWinner(
    showRef: GiveawayAvailableShowRef,
    giveawayId: string,
    giveawayInfo: {
      winnerId: string;
      quantity?: number;
      subtotal?: number;
      needShippingFee: boolean;
    },
  ): Promise<void> {
    const sendRequest = entityRefSelect(showRef, {
      liveShow: s => () =>
        this.liveStreamGiveawayItemSvc.liveStreamGiveawayItemApiControllerCreateGiveawayWinner(
          {
            liveStreamId: s.liveStreamId,
            giveawayItemId: giveawayId,
            liveStreamCreateGiveawayWinnerRequest: {
              needShippingFee: giveawayInfo.needShippingFee,
              quantity: giveawayInfo.quantity,
              price: giveawayInfo.subtotal,
              winnerIds: [giveawayInfo.winnerId],
            },
          },
        ),
      upcomingShow: s => () =>
        this.upcomingShowGiveawayItemSvc.upcomingShowGiveawayItemApiControllerCreateGiveawayWinner(
          {
            upcomingShowId: s.upcomingShowId,
            giveawayItemId: giveawayId,
            upcomingShowCreateGiveawayWinnerRequest: {
              needShippingFee: giveawayInfo.needShippingFee,
              quantity: giveawayInfo.quantity,
              price: giveawayInfo.subtotal,
              winnerIds: [giveawayInfo.winnerId],
            },
          },
        ),
    });

    await sendRequest();
  }
}

const transformConditionError = (
  fromCriteriaEnum: (type: string) => GiveawayCondition,
  err: any,
): GiveawayCondition[] => {
  if (err instanceof APIErrorWrapper) {
    const errInfo = err.details?.find(isErrorInfo);
    if (
      errInfo &&
      errInfo.reason === ErrorReasons.GIVEAWAY_CRITERIA_FAILED_ERROR
    ) {
      return [fromCriteriaEnum(errInfo.metadata.criteriaType)];
    }
  }

  throw err;
};
